import { Extension } from '@tiptap/core';

/**
 * Make Tab / Shift+Tab inside bullet/ordered lists always behave like
 * indent / outdent — and never escape the editor.
 *
 * Tiptap's built-in ListItem extension already binds Tab to `sinkListItem`,
 * but that command returns `false` when the current item can't be sunk
 * (e.g. it's the first or only item in its list). When a Tiptap keyboard
 * shortcut returns false, ProseMirror lets the keydown bubble to the
 * browser, and the browser's default Tab behavior moves focus to the next
 * focusable element on the page. In our editor, that next element is the
 * delete-row ✕ button on a script row, which is jarring.
 *
 * This extension wraps the indent/outdent commands so Tab is always
 * consumed when the cursor is inside a listItem — the indent runs if it
 * can, otherwise the keystroke is swallowed silently and focus stays put.
 *
 * Outside list items, Tab is left alone so accessibility tab-navigation
 * across the rest of the page (toolbar buttons, etc.) keeps working.
 */
export const ListTabKeymap = Extension.create({
  name: 'listTabKeymap',

  addKeyboardShortcuts() {
    const isInListItem = () => {
      const { $from } = this.editor.state.selection;
      for (let depth = $from.depth; depth > 0; depth--) {
        if ($from.node(depth).type.name === 'listItem') return true;
      }
      return false;
    };

    return {
      Tab: () => {
        if (!isInListItem()) return false;
        // Try to indent. If the command can't run (e.g. nothing to sink
        // into), still return true so the Tab is eaten and focus stays.
        this.editor.chain().focus().sinkListItem('listItem').run();
        return true;
      },
      'Shift-Tab': () => {
        if (!isInListItem()) return false;
        this.editor.chain().focus().liftListItem('listItem').run();
        return true;
      },
    };
  },
});
